import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@artstorefronts/ui';
import { useNavigate } from 'react-router-dom';
import { PencilIcon, ImageIcon, SendIcon, Loader2 } from 'lucide-react';

import { useAuth } from '~/context/auth';
import { useLibraryImage } from '~/hooks/useLibraryImages';
import ArtworkDetailsForm from '~/components/ArtworkDetailsForm';
import { Mocks } from '~/components/Mocks';
import { useAnalytics } from '~/context/analytics';
import { Completion } from '~/types';
import { SignUpModal } from '~/components/AuthModals';
import { useChatUI } from '~/context/chatUI';

// Helper function to detect if an image URL is a user image
function detectUserImage(
  src: string,
  currentUserId?: number,
  attachmentImageId?: string
): { isUserImage: boolean; detectedImageId?: string } {
  // If we have an imageId from the attachment object, use it directly
  if (attachmentImageId != null && attachmentImageId !== '') {
    return { isUserImage: true, detectedImageId: String(attachmentImageId) };
  }

  // Check for optimized URL pattern: https://domain.com/optimized/imageId
  const optimizedUrlPattern = /\/optimized\/(\d+)/;
  const match = src.match(optimizedUrlPattern);

  if (match) {
    return { isUserImage: true, detectedImageId: match[1] };
  }

  // Check for thumbnail URL pattern: https://domain.com/thumbnail/imageId
  const thumbnailUrlPattern = /\/thumbnail\/(\d+)/;
  const thumbnailMatch = src.match(thumbnailUrlPattern);

  if (thumbnailMatch) {
    return { isUserImage: true, detectedImageId: thumbnailMatch[1] };
  }

  // Check for attachment URL pattern: https://domain.com/<user-id>/imageId
  const attachmentUrlPattern = /\/(\d+)\/(\d+)/;
  const attachmentMatch = src.match(attachmentUrlPattern);
  if (attachmentMatch) {
    const urlUserId = parseInt(attachmentMatch[1]);
    const imageId = attachmentMatch[2];

    // Only consider it a user image if the user ID in the URL matches the current user's ID
    if (currentUserId && urlUserId === currentUserId) {
      return { isUserImage: true, detectedImageId: imageId };
    }
  }

  return { isUserImage: false };
}

export function UserImageComponent({
  src,
  alt,
  imageId,
  reversed = false,
  isAttachment = false,
  ...props
}: {
  src: string;
  alt?: string;
  imageId?: string;
  reversed?: boolean;
  isAttachment?: boolean;
  [key: string]: any;
}) {
  const { t } = useTranslation('chat');
  const { amplitude, intercom } = useAnalytics();
  const { user } = useAuth();
  const { mode } = useChatUI();
  const [modalState, setModalState] = useState({
    editDetailsOpen: false,
    mocksOpen: false,
    signUpOpen: false,
    isLoading: true,
  });
  const navigate = useNavigate();

  const { isUserImage, detectedImageId } = detectUserImage(src, user?.user_id, isAttachment ? imageId : undefined);
  const resolvedImageId = (imageId ? String(imageId) : undefined) ?? detectedImageId;

  // The viewer's own row for it (loaded page, else looked up by id); null for anyone else's
  const imageData = useLibraryImage(user ? resolvedImageId : null);
  const gallerySrc = imageData?.optimizedUrl || imageData?.thumbnailUrl;
  const displaySrc = gallerySrc || src || '';

  // If this is not a user image and not a processing attachment, render as regular image
  // UNLESS we're in public mode, then always show the full component with buttons
  if (!isUserImage && !(isAttachment && !imageData) && mode !== 'public') {
    if (!src) {
      return null;
    }

    return (
      <div className="w-40 h-40 flex items-center justify-center bg-gray-50 rounded-lg">
        <img src={src} alt={alt} className="max-h-full max-w-full h-auto w-auto rounded-lg" {...props} />
      </div>
    );
  }

  const handleEditDetailsClick = () => {
    amplitude.track('chat_imagebox_edit_details_clicked');
    intercom.trackEvent('chat_imagebox_edit_details_clicked');

    if (!user) {
      setModalState((prev) => ({ ...prev, signUpOpen: true }));
      return;
    }

    if (!imageData) {
      return;
    }

    setModalState((prev) => ({ ...prev, editDetailsOpen: true }));
  };

  const handleRoomMockupsClick = () => {
    amplitude.track('chat_imagebox_room_mockups_clicked');
    intercom.trackEvent('chat_imagebox_room_mockups_clicked');

    if (!user) {
      setModalState((prev) => ({ ...prev, signUpOpen: true }));
      return;
    }

    if (!imageData) {
      return;
    }

    setModalState((prev) => ({ ...prev, mocksOpen: true }));
  };

  const handlePostClick = () => {
    amplitude.track('chat_imagebox_new_post_clicked');
    intercom.trackEvent('chat_imagebox_new_post_clicked');

    if (!user) {
      setModalState((prev) => ({ ...prev, signUpOpen: true }));
      return;
    }

    if (!imageData) {
      return;
    }

    const completion: Completion = {
      id: '',
      content: '',
      image: imageData!,
      responseHtml: '',
    };

    navigate('/post', {
      state: {
        completion: completion,
        from: window.location.pathname,
      },
    });
  };

  if (mode === 'public' && !displaySrc) {
    return null;
  }

  // Signed-in viewers on shared chats only get actions when the artwork is in their gallery.
  if (mode === 'public' && user && !imageData) {
    return (
      <div className="w-40 h-40 flex items-center justify-center bg-gray-50 rounded-lg my-4">
        <img src={displaySrc} alt={alt} className="max-h-full max-w-full h-auto w-auto rounded-lg" {...props} />
      </div>
    );
  }

  // Authenticated users need gallery metadata before actions run; guests can still open sign-up
  const shouldDisableButtons = !!user && !imageData;
  const buttonProps = shouldDisableButtons
    ? { disabled: true, className: 'text-xs opacity-50' }
    : { className: 'text-xs' };

  return (
    <>
      <span className={`flex flex-col items-center justify-center bg-gray-50 p-4 rounded-lg w-full sm:w-fit`}>
        <span
          className={`inline-flex flex-row sm:flex-row items-center gap-4 w-full sm:w-fit ${reversed ? 'sm:flex-row-reverse' : ''} ${reversed ? 'flex-row-reverse' : ''}`}
        >
          <span className="w-40 h-40 flex items-center justify-center bg-gray-50 rounded-lg">
            {modalState.isLoading && <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />}
            <img
              src={displaySrc}
              alt={alt || imageData?.title || t('image.processing-image')}
              className={`max-h-full max-w-full h-auto w-auto rounded-lg ${modalState.isLoading ? 'hidden' : ''}`}
              onLoad={() => setModalState((prev) => ({ ...prev, isLoading: false }))}
              {...props}
            />
          </span>
          <span className={`flex flex-col sm:flex-row gap-2 ${reversed ? 'justify-end' : ''}`}>
            <Button
              size="sm"
              variant="outline"
              onClick={shouldDisableButtons ? undefined : handleEditDetailsClick}
              {...buttonProps}
            >
              <PencilIcon className="w-4 h-4 mr-1" /> {t('image.edit-details')}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={shouldDisableButtons ? undefined : handleRoomMockupsClick}
              {...buttonProps}
            >
              <ImageIcon className="w-4 h-4 mr-1" /> {t('image.room-mockups')}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={shouldDisableButtons ? undefined : handlePostClick}
              {...buttonProps}
            >
              <SendIcon className="w-4 h-4 mr-1" /> {t('image.post-to-social')}
            </Button>
          </span>
        </span>
      </span>

      {/* Render modals in portal to avoid DOM nesting issues */}
      {modalState.editDetailsOpen &&
        imageData &&
        createPortal(
          <ArtworkDetailsForm
            image={imageData}
            open={modalState.editDetailsOpen}
            onOpenChange={(open) => setModalState((prev) => ({ ...prev, editDetailsOpen: open }))}
            type="sheet"
            contentType="image"
          />,
          document.body
        )}

      {modalState.mocksOpen &&
        imageData &&
        user &&
        resolvedImageId &&
        createPortal(
          <Mocks
            imageId={parseInt(resolvedImageId)}
            open={modalState.mocksOpen}
            setOpen={(open) => setModalState((prev) => ({ ...prev, mocksOpen: open }))}
            imageDimensions={{
              imageWidth: imageData.width || 0,
              imageHeight: imageData.height || 0,
              imageDimensionType: imageData.dimensionType || 'inches',
            }}
            imageOriginalWidth={imageData.originalWidth}
            imageOriginalHeight={imageData.originalHeight}
          />,
          document.body
        )}

      {modalState.signUpOpen &&
        createPortal(
          <SignUpModal
            open={modalState.signUpOpen}
            onOpenChange={(open) => setModalState((prev) => ({ ...prev, signUpOpen: open }))}
          />,
          document.body
        )}
    </>
  );
}
